import * as Sentry from '@sentry/react';
import React, { lazy, Suspense, useCallback, useEffect, useMemo, useState } from 'react';
import {
	createBrowserRouter,
	createRoutesFromElements,
	Navigate,
	Outlet,
	Route,
} from 'react-router';
import { RouterProvider } from 'react-router/dom';

import { EntitlementsUpdates } from '~components/business-owner/entitlements/EntitlementsUpdates/EntitlementsUpdates';
import Folded2025 from '~components/business-owner/Folded2025/Folded2025';
import { PageVisitTracker } from '~components/business-owner/marketing/PageVisitTracker';
import { EmailLinkVerification, ImpersonationHandler } from '~components/clerk';
import BlockingLoader from '~components/commons/blocking-loader/blocking-loader';
import { ComponentWithLDUser } from '~components/ComponentWithLDUser/ComponentWithLDUser';
import PageNotFound from '~components/page-not-found';
import { RootRedirect } from '~components/RootRedirect';

import { useSessionStorage } from '~hooks/useSessionStorage';
import useTrackEvent from '~hooks/useTrackEvent';

import { getAccountInfo } from '~api/business-owner/account';
import { useClerkAuth } from '~api/business-owner/queries/auth/useClerkAuth';
import { useMessengerWebSocket } from '~api/business-owner/queries/messenger/useMessengerWebSocket';
import { useBarryWebsocket } from '~api/common/barry/queries/useBarryWebsocket';
import { useQtyUpdaterWebSocket } from '~api/websocket-connection/queries/useQtyUpdaterWebSocket';
import { ROLES } from '~constants';
import { INTERCOM_EVENTS, INTERCOM_EVENTS_TEMPLATES } from '~constants/intercom-events';
import { DASHBOARD_ROOT_ROUTE, GUEST_ROUTES } from '~constants/routes';
import { BoRouteAuthorization } from '~hocs/BoRouteAuthorization';
import { SessionState } from '~reducers/session';
import FullStory from '~third-party/fullstory';
import { setSurvicateTraits } from '~third-party/survicate';
import { SESSION_ENV_KEY } from '~utils/config';
import { isInternalEmail } from '~utils/email';
import { getIntercomBootData, getParsedLocalStorageData } from '~utils/functions';
import { retryImport } from '~utils/retryImport';

import { RouteErrorBoundary } from './RouteErrorBoundary';

// Lazy loads
const BODashboard = lazy(() => retryImport(() => import('./containers/bo-dashboard')));
const BOGlobalSettings = lazy(() =>
	retryImport(() => import('./containers/bo-gs-dashboard')),
);
const PasswordReset = lazy(() =>
	retryImport(() => import('./containers/password-reset')),
);
const Login = lazy(() => retryImport(() => import('./containers/login')));
const ForgotPassword = lazy(() =>
	retryImport(() => import('./containers/forgot-password')),
);
const BOMessenger = lazy(() => retryImport(() => import('./containers/bo-messenger')));
const BOInsurance = lazy(() => retryImport(() => import('./containers/bo-insurance')));
const FinancingUnit = lazy(() =>
	retryImport(() => import('~components/business-owner/FinancingUnit/FinancingUnit')),
);

const Spinner = (): React.JSX.Element => {
	return (
		<div
			style={{
				display: 'flex',
				justifyContent: 'center',
				alignItems: 'center',
				height: '100dvh',
				width: `100%`,
			}}
		>
			<span>Loading</span>
		</div>
	);
};

const SURVICATE_READY_EVENT = 'SurvicateReady';

const BusinessOwnerRoutes = (): React.JSX.Element => {
	useMessengerWebSocket();
	useQtyUpdaterWebSocket();
	useBarryWebsocket();

	return (
		<Suspense fallback={<BlockingLoader />}>
			<PageVisitTracker />
			<EntitlementsUpdates />
			<Outlet />
		</Suspense>
	);
};

const GuestRoutes = (): React.JSX.Element => {
	return (
		<Suspense fallback={<BlockingLoader />}>
			<Outlet />
		</Suspense>
	);
};

const AppRoutes = ({
	session,
	onSetSession,
}: {
	session: SessionState;
	onSetSession: (newSession: SessionState) => void;
}): React.JSX.Element => {
	const [isMounted, setIsMounted] = useState(false);
	const { boot, trackEvent } = useTrackEvent();
	const { isLoggedIn, roleName } = session;
	const [initialLink, setInitialLink] = useSessionStorage('initialLink', '') as [
		string,
		React.Dispatch<React.SetStateAction<string>>,
	];
	/**
	 * Memoize containers with BoRouteAuthorization
	 * so they're not re-created on every render (e.g. when LaunchDarkly flags change)
	 */
	const BODashboardWithAuthorization = useMemo(
		() =>
			BoRouteAuthorization({
				sessionUserRole: roleName,
				appliedRoles: [ROLES.owner, ROLES.admin, ROLES.manager, ROLES.installer],
				component: BODashboard,
			}),
		[roleName],
	);

	const BOGlobalSettingsWithAuthorization = useMemo(
		() =>
			BoRouteAuthorization({
				sessionUserRole: roleName,
				appliedRoles: [ROLES.owner, ROLES.admin],
				component: BOGlobalSettings,
			}),
		[roleName],
	);

	const BOMessengerWithAuthorization = useMemo(
		() =>
			BoRouteAuthorization({
				sessionUserRole: roleName,
				appliedRoles: [ROLES.owner, ROLES.admin, ROLES.manager],
				component: BOMessenger,
			}),
		[roleName],
	);

	const BOInsuranceWithAuthorization = useMemo(
		() =>
			BoRouteAuthorization({
				sessionUserRole: roleName,
				appliedRoles: [ROLES.owner, ROLES.admin, ROLES.manager],
				component: BOInsurance,
			}),
		[roleName],
	);

	const FinancingUnitWithAuthorization = useMemo(
		() =>
			BoRouteAuthorization({
				sessionUserRole: roleName,
				appliedRoles: [ROLES.owner, ROLES.admin],
				component: FinancingUnit,
			}),
		[roleName],
	);

	const Folded2025WithAuthorization = useMemo(
		() =>
			BoRouteAuthorization({
				sessionUserRole: roleName,
				appliedRoles: [ROLES.owner, ROLES.admin, ROLES.manager],
				component: Folded2025,
			}),
		[roleName],
	);

	const bootIntercom = useCallback(
		async (session) => {
			if (!session.uuid || !session.intercomHash) {
				//uuid/userHash might not be set in local storage if user hasn't done login for a long time, in this case request user info from server

				try {
					const {
						data: {
							email,
							uuid,
							firstName,
							lastName,
							intercomHash,
							business,
						},
					} = await getAccountInfo();

					boot(
						getIntercomBootData({
							email,
							uuid,
							firstName,
							lastName,
							intercomHash,
							business,
						}),
					);
					onSetSession({
						...session,
						email,
						firstName,
						lastName,
						uuid,
						intercomHash,
						business,
					});
				} catch (error) {
					Sentry.captureException(error);
				}
			} else {
				const { email, uuid, firstName, lastName, intercomHash, business } =
					session;

				boot(
					getIntercomBootData({
						email,
						uuid,
						firstName,
						lastName,
						intercomHash,
						business,
					}),
				);
			}
		},
		[onSetSession, boot],
	);

	useEffect(
		function identifyUserAndBusiness() {
			const { business, uuid, email, firstName, lastName } = session ?? {};
			if (uuid) {
				// the uuid on the session is the user. if this is present, we can assume the other user attributes are present on the session

				FullStory.setUserVars({
					email,
					// because of our impersonation system, internal users need more specific keys to prevent the merging of sessions from different businesses
					// for external users, we still want to only use `uuid` so that it matches their id in intercom
					uid:
						email && isInternalEmail(email)
							? `${uuid}:${business?.id}`
							: uuid,
					displayName: `${firstName} ${lastName}`,
					businessId: business?.id,
					businessName: business?.name,
				});

				const setTraits = () => {
					setSurvicateTraits({
						email,
						uid: uuid,
						first_name: firstName,
						last_name: lastName,
						businessId: business?.id,
						businessName: business?.name,
						businessCreatedAt: business?.createdAt,
					});
				};
				if (window._sva) {
					// if the survicate script has already been loaded, set the traits
					setTraits();
				} else {
					// if the script has not been loaded, this listener will be triggered when it is
					// THEN we can set the traits
					window.addEventListener(SURVICATE_READY_EVENT, setTraits);
					return () => {
						window.removeEventListener(SURVICATE_READY_EVENT, setTraits);
					};
				}
			}
		},
		[session],
	);

	useEffect(() => {
		const isLinkMoveToGuestRoute = Object.values(GUEST_ROUTES).includes(
			window.location.pathname,
		);

		if (isLinkMoveToGuestRoute) {
			return;
		}

		const link = `${window.location.pathname}${window.location.search}`;
		setInitialLink(link);
	}, [setInitialLink]);

	useEffect(() => {
		// if already logged in using Clerk, ignore this effect
		const searchParams = new URLSearchParams(window.location.search);
		if (searchParams.get('clerkAuthentication')) {
			return;
		}

		setIsMounted(true);

		const {
			token,
			userId,
			teamMemberId,
			roleName,
			firstName,
			lastName,
			email,
			business,
			uuid,
			intercomHash,
		} = getParsedLocalStorageData(SESSION_ENV_KEY) as Partial<SessionState>;
		const session: SessionState = {
			isLoggedIn: !!token,
		};

		if (session.isLoggedIn) {
			session.token = token;
			session.userId = userId;
			session.teamMemberId = teamMemberId;
			session.roleName = roleName;
			session.firstName = firstName;
			session.lastName = lastName;
			session.email = email;
			session.intercomHash = intercomHash;
			session.business = business;
			session.uuid = uuid;

			onSetSession(session);
			bootIntercom(session);
		} else {
			onSetSession(session);
		}
	}, [onSetSession, bootIntercom]);

	useClerkAuth({
		onSuccess: (data) => {
			setIsMounted(true);
			if (data) {
				const newSession: SessionState = {
					isLoggedIn: !!data.token,
					token: data.token,
					userId: data.userId,
					teamMemberId: data.teamMemberId,
					roleName: data.roleName,
					firstName: data.firstName,
					lastName: data.lastName,
					email: data.email,
					intercomHash: data.intercomHash,
					business: data.business,
					uuid: data.uuid,
				};
				onSetSession(newSession);
				if (newSession.isLoggedIn) {
					bootIntercom(newSession);

					trackEvent(INTERCOM_EVENTS.signIn, INTERCOM_EVENTS_TEMPLATES.signIn);
				}
			}
		},
	});

	const router = useMemo(
		() =>
			createBrowserRouter(
				createRoutesFromElements(
					isLoggedIn ? (
						<Route
							element={<BusinessOwnerRoutes />}
							errorElement={<RouteErrorBoundary />}
						>
							<Route path={DASHBOARD_ROOT_ROUTE}>
								<Route
									index
									element={
										<Navigate to="analytics-dashboard" replace />
									}
								/>
								<Route
									path="*"
									element={
										<ComponentWithLDUser
											component={BODashboardWithAuthorization}
										/>
									}
								/>
							</Route>
							<Route path="/global-settings">
								<Route
									index
									element={<Navigate to="account" replace />}
								/>
								<Route
									path="*"
									element={
										<ComponentWithLDUser
											component={BOGlobalSettingsWithAuthorization}
										/>
									}
								/>
							</Route>

							<Route
								path="/messenger/*"
								element={
									<ComponentWithLDUser
										component={BOMessengerWithAuthorization}
									/>
								}
							/>

							<Route
								path="/insurance"
								element={
									<ComponentWithLDUser
										component={BOInsuranceWithAuthorization}
									/>
								}
							/>
							<Route
								path="/financing"
								element={
									<ComponentWithLDUser
										component={FinancingUnitWithAuthorization}
									/>
								}
							/>
							<Route
								path="/impersonate"
								element={<ImpersonationHandler />}
							/>
							<Route path="/password-reset" element={<PasswordReset />} />
							<Route path="/login" element={<Navigate to="/" replace />} />
							<Route
								path="/"
								element={
									<RootRedirect
										initialLink={initialLink}
										onInitialLinkRedirect={() => {
											setInitialLink('');
											sessionStorage.removeItem('initialLink');
										}}
									/>
								}
							/>
							<Route
								path="/folded/*"
								element={
									<ComponentWithLDUser
										component={Folded2025WithAuthorization}
									/>
								}
							/>
							<Route path="*" element={<PageNotFound />} />
						</Route>
					) : (
						<Route
							element={<GuestRoutes />}
							errorElement={<RouteErrorBoundary />}
						>
							<Route path={GUEST_ROUTES.login} element={<Login />} />
							<Route
								path={GUEST_ROUTES.verifyEmailLink}
								element={<EmailLinkVerification />}
							/>
							<Route
								path={GUEST_ROUTES.impersonate}
								element={<ImpersonationHandler />}
							/>
							<Route
								path={GUEST_ROUTES.resetPassword}
								element={<PasswordReset />}
							/>
							<Route
								path={GUEST_ROUTES.forgotPassword}
								element={<ForgotPassword />}
							/>
							<Route
								path={GUEST_ROUTES.default}
								element={<Navigate to={GUEST_ROUTES.login} replace />}
							/>
							<Route
								path="*"
								element={<Navigate to={GUEST_ROUTES.login} replace />}
							/>
						</Route>
					),
				),
			),
		[isLoggedIn, initialLink],
	);

	return isMounted ? <RouterProvider router={router} /> : <Spinner />;
};

export default AppRoutes;
